import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { Plus } from 'react-feather';
import type { Projects } from '@trinity/chat/api/interfaces';
import { Collapser, SeeMore } from '@trinity/chat-components/components/presentation/index.js';
import { Divider, SidebarHeader } from '@trinity/chat-components/components/presentation/Main/index.js';
import * as Tab from '@/components/presentation/Workspace.Tab.Pane';
import type { Manager, ProjectNotesEditorOnChange, State } from '@/components/ProjectNotes';
import { isProjectNotesEmpty, ProjectNotesEditor, ProjectNotesEditorContainer, Toolbar, useProjectNotesEditor } from '@/components/ProjectNotes';
import { Remirror } from '@/types';
import { cx } from '@trinity/chat-components';
import { SectionEditForm } from './Project.Notes.FormButtons';
import styles from './style/ProjectsTable.module.css';

type Props = {
  query: UseQueryResult<Projects.FetchProjectNoteItems.Response>;
  mutation: UseMutationResult<Projects.UpdateProjectNoteItems.Response>;
};

const defaultProjectNotesContent: Remirror.RootNode = {
  type: Remirror.NodeType.Doc,
  content: [{
    content: [],
    type: Remirror.NodeType.Paragraph,
  }],
};

export const ProjectNotes = (props: Props) => {
  const [editing, setEditing] = useState<number | null>(null);
  const [newItem, setNewItem] = useState<ProjectNotesItem | null>(null);

  const handleAddNewItem = useCallback(() => {
    setNewItem({
      title: '',
      description: defaultProjectNotesContent,
    });
  }, []);

  const handleCancelNewItem = useCallback(() => {
    setNewItem(null);
  }, []);

  return (
    <Tab.Pane.Pane>
      <SidebarHeader title="Project Notes">
        {!editing &&
          <AddButton
            disabled={!!editing || !!newItem}
            onClick={handleAddNewItem} />}
      </SidebarHeader>
      <Divider />
      <Box sx={{ paddingTop: 2, paddingBottom: 2 }}>
        <Grid
          container
          spacing={2}
          direction="row"
          alignItems="stretch">
          {(props.query.isFetched && !newItem && !editing && !props.query.data?.items?.length) &&
            <Empty>No notes added.</Empty>}
          {props.query.data.items.map((item, index) =>
            <ProjectNotesItemEditable
              key={index}
              canEdit={!newItem && editing === null || editing === index}
              editing={editing === index}
              index={index}
              item={item}
              mutation={props.mutation}
              query={props.query}
              setEditing={setEditing} />,
          )}
          {newItem &&
            <ProjectNotesItemNew
              item={newItem}
              mutation={props.mutation}
              query={props.query}
              onCancel={handleCancelNewItem} />
          }
        </Grid>
      </Box>
    </Tab.Pane.Pane>
  );
};

ProjectNotes.displayName = 'Project.Notes';

type AddButtonProps = {
  disabled?: boolean;
  onClick: () => void;
};

const AddButton = (props: AddButtonProps) => {

  const StyledAddButton = styled(Plus)(({ theme }) => ({
    color: theme.palette.primary.main,
  }));

  return (
    <IconButton
      size='small'
      disabled={props.disabled}
      onClick={props.onClick}>
      <StyledAddButton />
    </IconButton>
  );
};

type ProjectNotesItemEditableProps = {
  canEdit: boolean;
  editing: boolean;
  index: number;
  item: ProjectNotesItem;
  setEditing: (index: number | null) => void;
} & Props;

const ProjectNotesItemEditable = ({ index, mutation, query, ...props }: ProjectNotesItemEditableProps) => {
  const [title, setTitle] = useState<ProjectNotesItem['title']>(props.item.title);
  const [description, setDescription] = useState<ProjectNotesItem['description']>(props.item.description);

  const { manager, state } = useProjectNotesEditor({
    initialState: props.item.description || defaultProjectNotesContent,
  });

  const handleSave = useCallback(async () => {
    const items = query.data.items.map((item, i) => {
      if (i === index) {
        return {
          ...item,
          title,
          description,
        };
      }
      return item;
    });

    await mutation.mutateAsync({
      items,
    });
    props.setEditing(null);
  }, [description, index, mutation, props, query, title]);

  const handleDelete = useCallback(async (fn?: () => void) => {
    await mutation.mutateAsync({
      items: query.data.items.filter((x, i) => x.id !== props.item.id),
    });

    props.setEditing(null);
  }, [
    mutation,
    props,
    query.data?.items,
  ]);

  const handleCancel = useCallback(() => {
    if (manager.view) {
      const original = props.item.description || defaultProjectNotesContent;
      manager.view.updateState(manager.createState({
        content: original,
      }));

      setDescription(original);
    }

    props.setEditing(null);
  }, [manager, props]);

  return (
    <Grid
      item
      xs={5}>
      <Paper
        sx={{ position: 'relative', padding: 2 }}
        variant="outlined">
        <Box sx={{
          display: 'flex',
          position: 'absolute',
          top: 5,
          right: 5,
        }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ display: 'flex', alignItems: 'center' }}>
            {props.canEdit &&
              <SectionEditForm
                isEditing={props.editing}
                disabled={!props.canEdit || title?.trim?.()?.length === 0 || isProjectNotesEmpty(description)}
                onEdit={() => props.setEditing(index)}
                onSave={handleSave}
                onCancel={handleCancel} />}
            {(!props.editing && props.canEdit) &&
              <DeleteProjectNotesItem onClick={handleDelete} />}
          </Stack>
        </Box>
        <Box width="100%">
          <Box sx={{ marginBottom: 2 }}>
            <Tab.Item.TextLabel>Title</Tab.Item.TextLabel>
            {props.editing &&
              <TextField
                fullWidth
                type='text'
                onChange={e => setTitle(e.target.value)}
                value={title}
                variant='standard' />}
            {!props.editing &&
              <Tab.Item.Value>
                <SeeMoreStyled
                  CollapserComp={Collapser}
                  lineHeight={19}
                  maxLines={2}>
                  {props.item.title}
                </SeeMoreStyled>
              </Tab.Item.Value>}
          </Box>
          <Box sx={{ marginBottom: 2 }}>
            <Tab.Item.TextLabel>Description</Tab.Item.TextLabel>
            <ProjectNotesItemDescription
              editing={props.editing}
              manager={manager}
              state={state}
              setValue={setDescription} />
          </Box>
        </Box>
      </Paper>
    </Grid>
  );
};

const SeeMoreStyled = styled(SeeMore)`
  line-break: anywhere;
`;

type ProjectNotesItemNewProps = {
  item: ProjectNotesItem;
  onCancel: () => void;
} & Props;

const ProjectNotesItemNew = ({ mutation, query, onCancel, ...props }: ProjectNotesItemNewProps) => {
  const [title, setTitle] = useState<ProjectNotesItem['title']>(props.item.title);
  const [description, setDescription] = useState<ProjectNotesItem['description']>(props.item.description);

  const { manager, state } = useProjectNotesEditor({
    initialState: props.item.description || defaultProjectNotesContent,
  });

  const handleSubmit = useCallback(async () => {
    await mutation.mutateAsync({
      items: query.data.items.concat({
        id: undefined,
        title,
        description,
      }),
    });
    onCancel();
  }, [description, mutation, query, title, onCancel]);

  return (
    <Grid
      item
      xs={5}>
      <Paper
        sx={{ position: 'relative', padding: 2 }}
        variant="outlined">
        <Box sx={{ position: 'absolute', top: 5, right: 5 }}>
          <SectionEditForm
            disabled={title?.trim?.()?.length === 0 || isProjectNotesEmpty(description)}
            isEditing
            onEdit={() => null}
            onSave={handleSubmit}
            onCancel={onCancel} />
        </Box>
        <Box width="100%">
          <Box sx={{ marginBottom: 1 }}>
            <Tab.Item.TextLabel>Title</Tab.Item.TextLabel>
            <TextField
              fullWidth
              variant='standard'
              onChange={e => setTitle(e.target.value)}
              value={title} />
          </Box>
          <Box sx={{ marginBottom: 1 }}>
            <Tab.Item.TextLabel>Description</Tab.Item.TextLabel>
            <ProjectNotesItemDescription
              editing
              manager={manager}
              state={state}
              setValue={setDescription} />
          </Box>
        </Box>
      </Paper>
    </Grid>
  );
};

const Button = styled.button(({ theme }) => ({
  color: theme?.palette?.primary?.main ?? 'var(--pri-02)',
  fontFamily: 'var(--font-bold)',
  fontSize: 14,
}));

type ProjectNotesItem = {
  id?: number;
  title: string;
  description: Remirror.RootNode;
};

const DeleteProjectNotesItem = ({ onClick }: { onClick: () => void }) => {
  return (
    <>
      <IconButton
        size='small'
        onClick={onClick}>
        <DeleteButton />
      </IconButton>
    </>
  );
};

type ProjectNotesItemDescriptionProps = {
  editing: boolean;
  manager: Manager;
  state: State;
  setValue: (value: Remirror.RootNode) => void;
};

const ProjectNotesItemDescription = ({ editing, manager, setValue, state, ...props }: ProjectNotesItemDescriptionProps) => {
  const [expanded, setExpanded] = useState(false);
  const handleChange: ProjectNotesEditorOnChange = useCallback(params => {

    if (!params.tr?.docChanged) return;

    const value = params.state.doc.toJSON() as Remirror.RootNode;

    setValue(value);
  }, [setValue]);

  useEffect(() => {
    if (!editing) {
      setExpanded(false);
    }
  }, [editing]);

  return (
    <ProjectNotesEditorContainer
      onChange={handleChange}
      editable={editing}
      manager={manager}
      state={state}>
      {editing && <RichTextToolbar />}
      <ProjectNotesEditor className={cx({ [styles.readonly]: !editing && !expanded })} />
      {!editing &&
        <SeeMoreExpanded onClick={() => setExpanded(!expanded)}>
          {expanded ? 'See Less' : 'See More'}
        </SeeMoreExpanded>}
    </ProjectNotesEditorContainer>
  );
};

const RichTextToolbar = styled(Toolbar)(({ theme }) => ({
  marginBottom: 10,

}));

const DeleteButton = styled(DeleteIcon)(({ theme }) => ({
  color: theme.palette.red.main,
  cursor: 'pointer',
}));

const Empty = styled.div(({ theme }) => ({
  color: theme.palette.gray.main,
  fontSize: '14px',
  margin: '6px 18px',
}));

const SeeMoreExpanded = styled.div(({ theme }) => ({
  padding: '4px 8px',
  display: 'flex',
  justifyContent: 'flex-end',
  color: theme.palette.gray.main,
  fontSize: '14px',
  cursor: 'pointer',

  '&:hover': {
    color: theme.palette.sentiment.primary.main,
    textDecoration: 'underline',
  },
}));